import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "aap_cart_v1";

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const keyOf = (productId, variantId) => `${productId}::${variantId || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota errors */
    }
  }, [items]);

  const addItem = (item, qty = 1, { open = true } = {}) => {
    const key = keyOf(item.product_id, item.variant_id);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      const cap = item.max_qty ?? existing?.max_qty ?? null;
      if (existing) {
        const next = cap != null ? Math.min(existing.quantity + qty, cap) : existing.quantity + qty;
        return prev.map((i) => (i.key === key ? { ...i, ...item, quantity: next } : i));
      }
      const first = cap != null ? Math.min(qty, cap) : qty;
      return [...prev, { ...item, key, quantity: first }];
    });
    if (open) setOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const updateQty = (key, qty) =>
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.key !== key) return i;
          const cap = i.max_qty ?? null;
          const clamped = cap != null ? Math.min(Math.max(1, qty), cap) : Math.max(1, qty);
          return { ...i, quantity: clamped };
        })
        .filter((i) => i.quantity > 0)
    );

  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((n, i) => n + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);

  const value = { items, addItem, removeItem, updateQty, clear, count, subtotal, open, setOpen };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
